import { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa';
import { IoSearchOutline, IoClose } from 'react-icons/io5';
import {
  MdDownload,
  MdFilterList,
  MdPersonAdd,
  MdTrendingDown,
  MdTrendingUp,
  MdRemove,
} from 'react-icons/md';
import { useUser } from '../context/UserContext';
import { consultarAnalisisPrecios } from '../api/apiConfig';
import { formatNumberEsCL } from '../utils/numberFormat';

const SIN_PLATAFORMA = 'Sin plataforma';
const SIN_EJECUTIVO = 'Sin ejecutivo';

const labelPlataforma = (v) => {
  const s = v == null ? '' : String(v).trim();
  return s || SIN_PLATAFORMA;
};

const labelEjecutivo = (v) => {
  const s = v == null ? '' : String(v).trim();
  return s || SIN_EJECUTIVO;
};

const uniqueSorted = (values) =>
  [...new Set(values)].sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));

const toggleInList = (arr, value) =>
  arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value];

const COLUMNAS = [
  { key: 'RUT_CLI', label: 'RUT', type: 'text' },
  { key: 'RAZON_SOCIAL', label: 'Razón social', type: 'text' },
  { key: 'PLATAFORMA', label: 'Plataforma', type: 'text' },
  { key: 'EJECUTIVO', label: 'Ejecutivo', type: 'text' },
  { key: 'VOLUMEN', label: 'Volumen', type: 'number' },
  { key: 'ULT_VOLUMEN', label: 'Volumen mes prev.', type: 'number' },
  { key: 'DELTA_VOLUMEN', label: 'Δ Volumen', type: 'number' },
  { key: 'DELTA_VOLUMEN_PCT', label: 'Δ Volumen %', type: 'number' },
  { key: 'ULT_OPE_FECHA', label: 'Fecha ult ope antes de Mes consultado', type: 'text' },
  { key: 'DIAS_SIN_OPERAR', label: 'Dias sin Operar', type: 'number' },
  { key: 'TASA_PONDERADA', label: 'Tasa ponderada', type: 'number' },
  { key: 'ULT_TASA_PONDERADA', label: 'Tasa pond. mes prev.', type: 'number' },
  { key: 'DELTA_TASA', label: 'Δ Tasa', type: 'number' },
  { key: 'DELTA_TASA_PCT', label: 'Δ Tasa %', type: 'number' },
];

const pad2 = (n) => String(n).padStart(2, '0');

const defaultPeriodoMesActual = () => {
  const now = new Date();
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}`;
};

const formatTasa = (v) => {
  if (v == null || v === '') return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  return formatNumberEsCL(n, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
};

const formatMonto = (v) => {
  if (v == null || v === '') return '';
  return formatNumberEsCL(Number(v) || 0, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const formatSigned = (v, { decimals = 0, suffix = '' } = {}) => {
  if (v == null || !Number.isFinite(Number(v))) return '';
  const n = Number(v);
  const abs = formatNumberEsCL(Math.abs(n), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  if (n > 0) return `+${abs}${suffix}`;
  if (n < 0) return `-${abs}${suffix}`;
  return `0${suffix}`;
};

const deltaClass = (v) => {
  if (v == null || !Number.isFinite(Number(v))) return '';
  const n = Number(v);
  if (n > 0) return 'text-emerald-700';
  if (n < 0) return 'text-red-700';
  return 'text-gray-600';
};

const calcDelta = (actual, previo) => {
  const a = Number(actual);
  const p = Number(previo);
  if (!Number.isFinite(a) || !Number.isFinite(p)) return null;
  return a - p;
};

const calcDeltaPct = (actual, previo) => {
  const a = Number(actual);
  const p = Number(previo);
  if (!Number.isFinite(a) || !Number.isFinite(p) || p === 0) return null;
  return ((a - p) / Math.abs(p)) * 100;
};

const parseFechaLocal = (raw) => {
  if (!raw) return null;
  const s = String(raw).slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};

/** Días entre última op fuera del mes y la más reciente del mes consultado. */
const calcDiasSinOperar = (fecOpeMes, ultOpeAntes) => {
  const a = parseFechaLocal(fecOpeMes);
  const b = parseFechaLocal(ultOpeAntes);
  if (!a || !b) return null;
  return Math.round((a.getTime() - b.getTime()) / 86400000);
};

const enrichRow = (row) => {
  const volumen = Number(row.VOLUMEN);
  const ultVolumen = row.ULT_VOLUMEN != null ? Number(row.ULT_VOLUMEN) : null;
  const tasa = row.TASA_PONDERADA != null ? Number(row.TASA_PONDERADA) : null;
  const ultTasa = row.ULT_TASA_PONDERADA != null ? Number(row.ULT_TASA_PONDERADA) : null;
  const tienePrev =
    row.ULT_VOLUMEN != null ||
    row.ULT_TASA_PONDERADA != null ||
    row.ULT_ANIO != null;
  const fecOpeMes = row.FEC_OPE_MES ? String(row.FEC_OPE_MES).slice(0, 10) : '';
  const ultOpeFecha = row.ULT_OPE_FECHA ? String(row.ULT_OPE_FECHA).slice(0, 10) : '';

  return {
    ...row,
    FEC_OPE_MES: fecOpeMes,
    ULT_OPE_FECHA: ultOpeFecha,
    PLATAFORMA: labelPlataforma(row.PLATAFORMA),
    EJECUTIVO: labelEjecutivo(row.EJECUTIVO),
    tienePrev,
    DIAS_SIN_OPERAR: calcDiasSinOperar(fecOpeMes, ultOpeFecha),
    DELTA_VOLUMEN: tienePrev ? calcDelta(volumen, ultVolumen) : null,
    DELTA_VOLUMEN_PCT: tienePrev ? calcDeltaPct(volumen, ultVolumen) : null,
    DELTA_TASA: tienePrev ? calcDelta(tasa, ultTasa) : null,
    DELTA_TASA_PCT: tienePrev ? calcDeltaPct(tasa, ultTasa) : null,
  };
};

const TASA_EPS = 1e-9;

const classifyMovimientoTasa = (row) => {
  if (!row.tienePrev) return 'nuevos';
  const d = Number(row.DELTA_TASA);
  if (!Number.isFinite(d) || Math.abs(d) < TASA_EPS) return 'mantiene';
  return d < 0 ? 'bajaron' : 'sube';
};

const emptySegTasa = () => ({ n: 0, volumen: 0, ultVolumen: 0, diferencia: 0 });

const resumirMovimientoTasa = (rows) => {
  const segs = {
    bajaron: emptySegTasa(),
    mantiene: emptySegTasa(),
    sube: emptySegTasa(),
    nuevos: emptySegTasa(),
  };
  for (const row of rows) {
    const seg = segs[classifyMovimientoTasa(row)];
    seg.n += 1;
    seg.volumen += Number(row.VOLUMEN) || 0;
    seg.ultVolumen += Number(row.ULT_VOLUMEN) || 0;
  }
  for (const seg of Object.values(segs)) {
    seg.diferencia = seg.volumen - seg.ultVolumen;
  }
  return {
    ...segs,
    total: {
      n: segs.bajaron.n + segs.mantiene.n + segs.sube.n + segs.nuevos.n,
      volumen: segs.bajaron.volumen + segs.mantiene.volumen + segs.sube.volumen + segs.nuevos.volumen,
    },
  };
};

const KPI_TASA_CARDS = [
  {
    key: 'bajaron',
    title: 'Bajaron',
    hint: 'Tasa más baja que el mes previo',
    accent: 'var(--if-accent)',
    Icon: MdTrendingDown,
    iconWrap: 'bg-red-50 text-red-600',
  },
  {
    key: 'mantiene',
    title: 'Mantiene',
    hint: 'Misma tasa que el mes previo',
    accent: 'var(--if-gray-400)',
    Icon: MdRemove,
    iconWrap: 'bg-slate-100 text-slate-600',
  },
  {
    key: 'sube',
    title: 'Sube',
    hint: 'Tasa más alta que el mes previo',
    accent: 'var(--if-success)',
    Icon: MdTrendingUp,
    iconWrap: 'bg-emerald-50 text-emerald-700',
  },
  {
    key: 'nuevos',
    title: 'Nuevos',
    hint: 'Sin operación previa en cartera',
    accent: 'var(--if-primary)',
    Icon: MdPersonAdd,
    iconWrap: 'bg-slate-100 text-slate-700',
  },
];

const cellValue = (row, key) => {
  if (key === 'ULT_OPE_FECHA') return row.ULT_OPE_FECHA || '';
  if (key === 'FEC_OPE_MES') return row.FEC_OPE_MES || '';
  return row[key];
};

const STICKY_RUT_W = 130;
const STICKY_RAZON_W = 240;

const stickyColStyle = (key, variant = 'body') => {
  if (key !== 'RUT_CLI' && key !== 'RAZON_SOCIAL') return undefined;
  const isRut = key === 'RUT_CLI';
  const isHeader = variant === 'header';
  return {
    position: 'sticky',
    left: isRut ? 0 : STICKY_RUT_W,
    top: isHeader ? 0 : undefined,
    width: isRut ? STICKY_RUT_W : STICKY_RAZON_W,
    minWidth: isRut ? STICKY_RUT_W : STICKY_RAZON_W,
    maxWidth: isRut ? STICKY_RUT_W : STICKY_RAZON_W,
    zIndex: isHeader ? 20 : 4,
    boxShadow: isRut ? undefined : '2px 0 6px -2px rgba(15, 23, 42, 0.18)',
  };
};

const AnalisisPrecios = () => {
  const { user } = useUser();
  const [periodo, setPeriodo] = useState(defaultPeriodoMesActual);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [plataformasSel, setPlataformasSel] = useState([]);
  const [ejecutivosSel, setEjecutivosSel] = useState([]);
  const [ejecutivoBusqueda, setEjecutivoBusqueda] = useState('');
  const [sortColumn, setSortColumn] = useState('VOLUMEN');
  const [sortDirection, setSortDirection] = useState('desc');

  const filasBase = useMemo(
    () => (result?.filas || []).map(enrichRow),
    [result],
  );

  const opcionesPlataforma = useMemo(
    () => uniqueSorted(filasBase.map((row) => row.PLATAFORMA)),
    [filasBase],
  );

  const opcionesEjecutivo = useMemo(() => {
    const origen = plataformasSel.length
      ? filasBase.filter((row) => plataformasSel.includes(row.PLATAFORMA))
      : filasBase;
    return uniqueSorted(origen.map((row) => row.EJECUTIVO));
  }, [filasBase, plataformasSel]);

  useEffect(() => {
    const allowed = new Set(opcionesEjecutivo);
    setEjecutivosSel((prev) => {
      const next = prev.filter((e) => allowed.has(e));
      return next.length === prev.length ? prev : next;
    });
  }, [opcionesEjecutivo]);

  const filasPorDimension = useMemo(() => {
    return filasBase.filter((row) => {
      if (plataformasSel.length && !plataformasSel.includes(row.PLATAFORMA)) return false;
      if (ejecutivosSel.length && !ejecutivosSel.includes(row.EJECUTIVO)) return false;
      return true;
    });
  }, [filasBase, plataformasSel, ejecutivosSel]);

  const filasFiltradas = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return filasPorDimension;
    return filasPorDimension.filter((row) =>
      COLUMNAS.some((col) => {
        const v = cellValue(row, col.key);
        return v != null && String(v).toLowerCase().includes(q);
      }),
    );
  }, [filasPorDimension, searchTerm]);

  const filas = useMemo(() => {
    if (!sortColumn) return filasFiltradas;
    const col = COLUMNAS.find((c) => c.key === sortColumn);
    const dir = sortDirection === 'asc' ? 1 : -1;
    return [...filasFiltradas].sort((a, b) => {
      let av = cellValue(a, sortColumn);
      let bv = cellValue(b, sortColumn);
      if (av == null || av === '') av = col?.type === 'number' ? -Infinity : '';
      if (bv == null || bv === '') bv = col?.type === 'number' ? -Infinity : '';
      if (col?.type === 'number') {
        const an = Number(av);
        const bn = Number(bv);
        const aOk = Number.isFinite(an);
        const bOk = Number.isFinite(bn);
        if (aOk && bOk) return dir * (an - bn);
        if (aOk) return dir;
        if (bOk) return -dir;
        return 0;
      }
      return dir * String(av).localeCompare(String(bv), 'es', { numeric: true, sensitivity: 'base' });
    });
  }, [filasFiltradas, sortColumn, sortDirection]);

  const totalVolumen = useMemo(
    () => filas.reduce((acc, row) => acc + (Number(row.VOLUMEN) || 0), 0),
    [filas],
  );
  const totalUltVolumen = useMemo(
    () => filas.reduce((acc, row) => acc + (Number(row.ULT_VOLUMEN) || 0), 0),
    [filas],
  );
  const totalDeltaVolumen = useMemo(
    () => totalVolumen - totalUltVolumen,
    [totalVolumen, totalUltVolumen],
  );
  const totalDeltaVolumenPct = useMemo(() => {
    if (!totalUltVolumen) return null;
    return (totalDeltaVolumen / Math.abs(totalUltVolumen)) * 100;
  }, [totalDeltaVolumen, totalUltVolumen]);

  const kpisTasa = useMemo(() => resumirMovimientoTasa(filas), [filas]);

  const hayFiltroActivo =
    plataformasSel.length > 0 || ejecutivosSel.length > 0 || Boolean(searchTerm.trim());

  const ejecutivosVisibles = useMemo(() => {
    const q = ejecutivoBusqueda.trim().toLowerCase();
    if (!q) return opcionesEjecutivo;
    return opcionesEjecutivo.filter((nombre) => nombre.toLowerCase().includes(q));
  }, [opcionesEjecutivo, ejecutivoBusqueda]);

  const limpiarFiltros = () => {
    setPlataformasSel([]);
    setEjecutivosSel([]);
    setEjecutivoBusqueda('');
    setSearchTerm('');
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return <FaSort className="inline ml-1 opacity-30" />;
    return sortDirection === 'asc' ? (
      <FaSortUp className="inline ml-1" />
    ) : (
      <FaSortDown className="inline ml-1" />
    );
  };

  const consultar = async (e) => {
    e?.preventDefault?.();
    setError('');
    setResult(null);
    setSearchTerm('');
    setPlataformasSel([]);
    setEjecutivosSel([]);
    setEjecutivoBusqueda('');

    if (!periodo || !/^\d{4}-\d{2}$/.test(periodo)) {
      setError('Selecciona un mes.');
      return;
    }

    const [anioStr, mesStr] = periodo.split('-');
    const anio = Number(anioStr);
    const mes = Number(mesStr);
    if (!anio || mes < 1 || mes > 12) {
      setError('Mes inválido.');
      return;
    }

    try {
      setLoading(true);
      const resp = await consultarAnalisisPrecios(user, { anio, mes });

      let data = resp;
      if (data?.body && typeof data.body === 'string') {
        try {
          data = JSON.parse(data.body);
        } catch {
          /* keep */
        }
      }
      if (data?.error) {
        throw new Error(data.message || data.error);
      }
      if (!data?.filas && !Array.isArray(data)) {
        throw new Error(data?.message || 'Respuesta inesperada del servidor.');
      }
      if (Array.isArray(data)) {
        data = { anio, mes, filas: data };
      }
      setResult(data);
      setSortColumn('VOLUMEN');
      setSortDirection('desc');
    } catch (err) {
      console.error(err);
      setError(err?.message || 'No se pudo consultar el análisis de precios.');
    } finally {
      setLoading(false);
    }
  };

  const exportarExcel = () => {
    if (!filas.length) return;
    const wb = XLSX.utils.book_new();
    const rows = filas.map((row) => ({
      RUT: row.RUT_CLI ?? '',
      'Razón social': row.RAZON_SOCIAL ?? '',
      Plataforma: row.PLATAFORMA ?? '',
      Ejecutivo: row.EJECUTIVO ?? '',
      Volumen: Number(row.VOLUMEN) || 0,
      'Volumen mes prev.': row.ULT_VOLUMEN != null ? Number(row.ULT_VOLUMEN) : '',
      'Δ Volumen': row.DELTA_VOLUMEN != null ? Number(row.DELTA_VOLUMEN) : '',
      'Δ Volumen %': row.DELTA_VOLUMEN_PCT != null ? Number(row.DELTA_VOLUMEN_PCT) : '',
      'Fecha ult ope antes de Mes consultado': row.ULT_OPE_FECHA || '',
      'Dias sin Operar': row.DIAS_SIN_OPERAR != null ? Number(row.DIAS_SIN_OPERAR) : '',
      'Tasa ponderada': row.TASA_PONDERADA != null ? Number(row.TASA_PONDERADA) : '',
      'Tasa pond. mes prev.': row.ULT_TASA_PONDERADA != null ? Number(row.ULT_TASA_PONDERADA) : '',
      'Δ Tasa': row.DELTA_TASA != null ? Number(row.DELTA_TASA) : '',
      'Δ Tasa %': row.DELTA_TASA_PCT != null ? Number(row.DELTA_TASA_PCT) : '',
    }));
    rows.push({
      RUT: 'TOTAL',
      'Razón social': '',
      Plataforma: '',
      Ejecutivo: '',
      Volumen: totalVolumen,
      'Volumen mes prev.': totalUltVolumen,
      'Δ Volumen': totalDeltaVolumen,
      'Δ Volumen %': totalDeltaVolumenPct != null ? totalDeltaVolumenPct : '',
      'Fecha ult ope antes de Mes consultado': '',
      'Dias sin Operar': '',
      'Tasa ponderada': '',
      'Tasa pond. mes prev.': '',
      'Δ Tasa': '',
      'Δ Tasa %': '',
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, 'Analisis precios');
    XLSX.writeFile(wb, `analisis_precios_${periodo}.xlsx`);
  };

  const renderCell = (row, key) => {
    switch (key) {
      case 'VOLUMEN':
      case 'ULT_VOLUMEN':
        return formatMonto(row[key]);
      case 'DELTA_VOLUMEN':
        return (
          <span className={deltaClass(row.DELTA_VOLUMEN)}>
            {formatSigned(row.DELTA_VOLUMEN, { decimals: 0 })}
          </span>
        );
      case 'DELTA_VOLUMEN_PCT':
        return (
          <span className={deltaClass(row.DELTA_VOLUMEN_PCT)}>
            {formatSigned(row.DELTA_VOLUMEN_PCT, { decimals: 1, suffix: '%' })}
          </span>
        );
      case 'DIAS_SIN_OPERAR':
        return row.DIAS_SIN_OPERAR != null && Number.isFinite(Number(row.DIAS_SIN_OPERAR))
          ? formatNumberEsCL(Number(row.DIAS_SIN_OPERAR), {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })
          : '';
      case 'TASA_PONDERADA':
      case 'ULT_TASA_PONDERADA':
        return formatTasa(row[key]);
      case 'DELTA_TASA':
        return (
          <span className={deltaClass(row.DELTA_TASA)}>
            {formatSigned(row.DELTA_TASA, { decimals: 2 })}
          </span>
        );
      case 'DELTA_TASA_PCT':
        return (
          <span className={deltaClass(row.DELTA_TASA_PCT)}>
            {formatSigned(row.DELTA_TASA_PCT, { decimals: 3, suffix: '%' })}
          </span>
        );
      case 'ULT_OPE_FECHA':
        return row.ULT_OPE_FECHA || '';
      case 'RAZON_SOCIAL':
        return row.RAZON_SOCIAL ?? '';
      case 'PLATAFORMA':
        return row.PLATAFORMA ?? '';
      case 'EJECUTIVO':
        return row.EJECUTIVO ?? '';
      default:
        return row[key] ?? '';
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-baseline gap-4 mb-6">
        <h1 className="panel-title mb-0">Análisis de precios</h1>
      </div>

      <div className="seccion-datos mb-6">
        <p className="text-sm text-gray-600 mb-4 leading-relaxed">
          Clientes que operaron en el mes elegido: volumen (<code>MON_ANT</code>) y tasa ponderada.
          El mes de comparación es el mes calendario de la última operación previa; ahí se suman
          volumen y tasa. <strong>Dias sin Operar</strong> = días entre la última op. fuera del mes
          y la operación más reciente del mes consultado. Las columnas Δ muestran variación (+ / −).
        </p>

        <form onSubmit={consultar} className="flex flex-wrap items-end gap-3">
          <div className="w-full max-w-xs">
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
              Mes
            </label>
            <input
              type="month"
              value={periodo}
              onChange={(e) => setPeriodo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400"
            />
          </div>
          <button type="submit" disabled={loading} className="btn-primary disabled:opacity-50">
            {loading ? 'Consultando...' : 'Consultar'}
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-50"
            disabled={!filas.length}
            onClick={exportarExcel}
          >
            <MdDownload className="w-4 h-4" />
            Exportar Excel
          </button>
        </form>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mt-4 text-sm">
            {error}
          </div>
        )}
      </div>

      {result && (
        <div className="seccion-datos">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
            <div>
              <h2 className="text-lg font-semibold mb-1" style={{ color: 'var(--if-primary)' }}>
                Resultados
              </h2>
              <p className="text-sm text-slate-600">
                {hayFiltroActivo
                  ? `Mostrando ${filas.length.toLocaleString('es-CL')} de ${filasBase.length.toLocaleString('es-CL')}`
                  : filas.length.toLocaleString('es-CL')}
                {' '}clientes
                {' · '}Volumen{' '}
                <strong className="text-slate-800">{formatMonto(totalVolumen)}</strong>
                {' · '}Δ Vol{' '}
                <strong className={deltaClass(totalDeltaVolumen)}>
                  {formatSigned(totalDeltaVolumen, { decimals: 0 })}
                </strong>
              </p>
            </div>
            <div className="relative min-w-[220px] max-w-md w-full sm:w-72">
              <IoSearchOutline className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar RUT, cliente, ejecutivo..."
                className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
              />
              {searchTerm && (
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  onClick={() => setSearchTerm('')}
                  aria-label="Limpiar búsqueda"
                >
                  <IoClose className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {filasBase.length > 0 && (
            <div className="mb-5 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <MdFilterList className="w-4 h-4" style={{ color: 'var(--if-primary)' }} />
                  Filtros
                </div>
                {hayFiltroActivo && (
                  <button
                    type="button"
                    className="text-xs font-semibold text-slate-600 hover:text-slate-900 underline-offset-2 hover:underline"
                    onClick={limpiarFiltros}
                  >
                    Limpiar todo
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                    Plataforma
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {opcionesPlataforma.map((plat) => {
                      const on = plataformasSel.includes(plat);
                      return (
                        <button
                          key={plat}
                          type="button"
                          onClick={() => setPlataformasSel((prev) => toggleInList(prev, plat))}
                          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                            on
                              ? 'text-white border-transparent'
                              : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'
                          }`}
                          style={on ? { background: 'var(--if-primary)' } : undefined}
                        >
                          {plat}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                    Ejecutivo
                    {ejecutivosSel.length > 0 && (
                      <span className="ml-2 font-medium normal-case tracking-normal text-slate-500">
                        {ejecutivosSel.length.toLocaleString('es-CL')} seleccionado(s)
                      </span>
                    )}
                  </p>
                  <div className="relative mb-2">
                    <IoSearchOutline className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <input
                      type="search"
                      value={ejecutivoBusqueda}
                      onChange={(e) => setEjecutivoBusqueda(e.target.value)}
                      placeholder="Buscar ejecutivo..."
                      className="w-full pl-9 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                    />
                  </div>
                  <div className="max-h-40 overflow-y-auto rounded-md border border-slate-200 bg-white divide-y divide-slate-100">
                    {ejecutivosVisibles.length === 0 ? (
                      <p className="px-3 py-3 text-xs text-slate-500">Sin ejecutivos para el filtro.</p>
                    ) : (
                      ejecutivosVisibles.map((ejec) => {
                        const on = ejecutivosSel.includes(ejec);
                        return (
                          <label
                            key={ejec}
                            className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer ${
                              on ? 'bg-slate-100 text-slate-900 font-medium' : 'text-slate-700 hover:bg-slate-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="rounded border-slate-300"
                              checked={on}
                              onChange={() => setEjecutivosSel((prev) => toggleInList(prev, ejec))}
                            />
                            <span className="truncate">{ejec}</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {filasBase.length > 0 && (
            <div className="mb-5">
              <div className="flex flex-wrap items-end justify-between gap-2 mb-3">
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--if-primary)' }}>
                    Movimiento de tasa
                  </p>
                  <p className="text-xs text-slate-500">
                    Clientes agrupados por Δ Tasa vs el mes de comparación.
                  </p>
                </div>
                <span className="text-xs text-slate-500">
                  {kpisTasa.total.n.toLocaleString('es-CL')} clientes
                  {' · '}
                  {formatMonto(kpisTasa.total.volumen)}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                {KPI_TASA_CARDS.map((card) => {
                  const seg = kpisTasa[card.key];
                  const pct = kpisTasa.total.volumen
                    ? (seg.volumen / kpisTasa.total.volumen) * 100
                    : 0;
                  const Icon = card.Icon;
                  return (
                    <div
                      key={card.key}
                      className="rounded-lg border border-slate-200 bg-white overflow-hidden"
                    >
                      <div className="h-1" style={{ background: card.accent }} />
                      <div className="p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-sm font-semibold text-slate-800">{card.title}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{card.hint}</p>
                          </div>
                          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-md ${card.iconWrap}`}>
                            <Icon className="w-4 h-4" />
                          </span>
                        </div>
                        <p className="mt-3 text-2xl font-semibold tabular-nums text-slate-900 leading-none">
                          {seg.n.toLocaleString('es-CL')}
                          <span className="ml-1 text-xs font-medium text-slate-500">clientes</span>
                        </p>
                        <dl className="mt-3 space-y-1 text-xs text-slate-600">
                          <div className="flex justify-between gap-2">
                            <dt>Volumen mes</dt>
                            <dd className="font-medium tabular-nums text-slate-800">{formatMonto(seg.volumen)}</dd>
                          </div>
                          <div className="flex justify-between gap-2">
                            <dt>Vol. mes prev.</dt>
                            <dd className="font-medium tabular-nums">{formatMonto(seg.ultVolumen)}</dd>
                          </div>
                          <div className="flex justify-between gap-2">
                            <dt>Diferencia</dt>
                            <dd className={`font-semibold tabular-nums ${deltaClass(seg.diferencia)}`}>
                              {formatSigned(seg.diferencia, { decimals: 0 })}
                            </dd>
                          </div>
                        </dl>
                        <div className="mt-3">
                          <div className="flex justify-between text-[11px] text-slate-500 mb-1">
                            <span>Peso en volumen</span>
                            <span className="tabular-nums">{formatNumberEsCL(pct, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%</span>
                          </div>
                          <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                            <div
                              className="h-full rounded-full"
                              style={{ width: `${Math.min(100, Math.max(0, pct))}%`, background: card.accent }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="tabla-container" style={{ maxHeight: '70vh', overflow: 'auto' }}>
            <table className="min-w-full text-sm" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
              <thead>
                <tr className="bg-slate-100 text-left">
                  {COLUMNAS.map((col) => (
                    <th
                      key={col.key}
                      className="px-3 py-2 font-semibold text-gray-700 whitespace-nowrap"
                      style={stickyColStyle(col.key, 'header')}
                    >
                      <button
                        type="button"
                        onClick={() => handleSort(col.key)}
                        className="inline-flex items-center hover:text-gray-900"
                      >
                        {col.label}
                        {getSortIcon(col.key)}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filas.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNAS.length} className="px-3 py-4 text-gray-500">
                      {filasBase.length === 0
                        ? 'Sin clientes con operaciones en el mes indicado.'
                        : hayFiltroActivo
                          ? 'Ningún resultado para los filtros aplicados.'
                          : 'Ningún resultado para la búsqueda.'}
                    </td>
                  </tr>
                ) : (
                  filas.map((row, idx) => (
                    <tr key={row.RUT_CLI || idx} className="group border-t border-gray-200 hover:bg-slate-50">
                      {COLUMNAS.map((col) => (
                        <td
                          key={col.key}
                          className={`px-3 py-2 ${
                            col.key === 'RAZON_SOCIAL' ? 'break-words' : 'whitespace-nowrap'
                          } ${
                            col.key === 'RUT_CLI' || col.key === 'RAZON_SOCIAL'
                              ? 'bg-white group-hover:bg-slate-50'
                              : ''
                          }`}
                          style={stickyColStyle(col.key)}
                        >
                          {renderCell(row, col.key)}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
              {filas.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
                    {COLUMNAS.map((col) => {
                      if (col.key === 'RUT_CLI') {
                        return (
                          <td key={col.key} className="px-3 py-2 whitespace-nowrap bg-slate-50" style={stickyColStyle(col.key, 'footer')}>
                            TOTAL
                          </td>
                        );
                      }
                      if (col.key === 'RAZON_SOCIAL') {
                        return (
                          <td key={col.key} className="px-3 py-2 bg-slate-50" style={stickyColStyle(col.key, 'footer')} />
                        );
                      }
                      if (col.key === 'VOLUMEN') {
                        return (
                          <td key={col.key} className="px-3 py-2 whitespace-nowrap">
                            {formatMonto(totalVolumen)}
                          </td>
                        );
                      }
                      if (col.key === 'ULT_VOLUMEN') {
                        return (
                          <td key={col.key} className="px-3 py-2 whitespace-nowrap">
                            {formatMonto(totalUltVolumen)}
                          </td>
                        );
                      }
                      if (col.key === 'DELTA_VOLUMEN') {
                        return (
                          <td key={col.key} className={`px-3 py-2 whitespace-nowrap ${deltaClass(totalDeltaVolumen)}`}>
                            {formatSigned(totalDeltaVolumen, { decimals: 0 })}
                          </td>
                        );
                      }
                      if (col.key === 'DELTA_VOLUMEN_PCT') {
                        return (
                          <td key={col.key} className={`px-3 py-2 whitespace-nowrap ${deltaClass(totalDeltaVolumenPct)}`}>
                            {formatSigned(totalDeltaVolumenPct, { decimals: 1, suffix: '%' })}
                          </td>
                        );
                      }
                      return <td key={col.key} className="px-3 py-2" />;
                    })}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalisisPrecios;
